import type { Quote, QuoteStatus, StockAvailability } from '@/services/quote.service';

type BadgeVariant = 'success' | 'warning' | 'draft' | 'danger' | 'info' | 'active' | 'pending';

export const QUOTE_STATUS_BADGE: Record<QuoteStatus, { label: string; variant: BadgeVariant }> = {
  ACTIVE: { label: 'Active', variant: 'info' },
  APPROVED: { label: 'Approved', variant: 'success' },
  CONVERTED: { label: 'Converted', variant: 'success' },
  EXPIRED: { label: 'Expired', variant: 'danger' },
  CANCELLED: { label: 'Cancelled', variant: 'draft' },
  REJECTED: { label: 'Declined', variant: 'danger' },
};

export const STOCK_INDICATOR: Record<StockAvailability, { label: string; color: string; dot: string }> = {
  IN_STOCK: {
    label: 'In Stock',
    color: 'text-success-600 dark:text-success-400',
    dot: 'bg-success-500',
  },
  LOW_STOCK: {
    label: 'Low Stock',
    color: 'text-warning-600 dark:text-warning-400',
    dot: 'bg-warning-500',
  },
  OUT_OF_STOCK: {
    label: 'Out of Stock',
    color: 'text-danger-600 dark:text-danger-400',
    dot: 'bg-danger-500',
  },
};

export const QUOTE_STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'CONVERTED', label: 'Converted' },
  { value: 'EXPIRED', label: 'Expired' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'REJECTED', label: 'Declined' },
];

/**
 * The customer's latest answer on the public quote link (T11), for the list
 * and detail pages: the decline reason, or the newest change request on a
 * quote that is still open. Null when there is nothing to show.
 */
export function quoteCustomerResponse(
  quote: Pick<Quote, 'status' | 'rejection_reason' | 'last_change_request'>,
): { kind: 'DECLINED' | 'CHANGES'; text: string } | null {
  if (quote.status === 'REJECTED' && quote.rejection_reason) {
    return { kind: 'DECLINED', text: quote.rejection_reason };
  }
  if (quote.status === 'ACTIVE' && quote.last_change_request) {
    return { kind: 'CHANGES', text: quote.last_change_request };
  }
  return null;
}

/**
 * Determine the worst stock availability across all items in a quote.
 * OUT_OF_STOCK > LOW_STOCK > IN_STOCK
 */
export function worstStockStatus(
  items: { availability: StockAvailability }[],
): StockAvailability {
  if (items.some((i) => i.availability === 'OUT_OF_STOCK')) return 'OUT_OF_STOCK';
  if (items.some((i) => i.availability === 'LOW_STOCK')) return 'LOW_STOCK';
  return 'IN_STOCK';
}

/**
 * Calculate days remaining until a quote expires.
 * Returns negative number if expired.
 */
export function daysUntilExpiry(validUntil: string): number {
  const now = new Date();
  const expiry = new Date(validUntil);
  const diffMs = expiry.getTime() - now.getTime();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

/**
 * Format the validity display string.
 */
export function formatValidity(validUntil: string): { text: string; urgency: 'normal' | 'warning' | 'danger' } {
  const days = daysUntilExpiry(validUntil);
  if (days < 0) return { text: 'Expired', urgency: 'danger' };
  if (days === 0) return { text: 'Expires today', urgency: 'warning' };
  if (days === 1) return { text: '1 day left', urgency: 'warning' };
  if (days <= 3) return { text: `${days} days left`, urgency: 'warning' };
  return { text: `${days} days left`, urgency: 'normal' };
}

// ─── Shared options (used by QuotationListPage + QuotationDetailPage) ──────

export const SEND_CHANNEL_OPTIONS = [
  { value: 'WHATSAPP', label: 'WhatsApp' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'LINK', label: 'Shareable Link' },
];

/** T10: how a down payment is taken against a quote. */
export const ADVANCE_METHOD_OPTIONS = [
  { value: 'CASH', label: 'Cash' },
  { value: 'CARD', label: 'Card' },
  { value: 'UPI', label: 'UPI' },
];

/** T10: how a cancelled quote's down payment goes back. */
export const REFUND_MODE_OPTIONS = [
  { value: 'ORIGINAL', label: 'To the original payment method' },
  { value: 'STORE_CREDIT', label: 'As store credit' },
];

// ─── Role level map (mirrors backend RolesGuard numeric hierarchy) ──────────

export const ROLE_LEVELS: Record<string, number> = {
  SUPER_ADMIN: 100,
  ADMIN: 80,
  MODERATOR: 60,
  OWNER: 50,
  MANAGER: 30,
  CASHIER: 5,
};
